// OmForm 백엔드: react-hook-form + zod. antd 미사용(FormInstance/Form.Item auto-wiring 대체).
// api/OmForm 이 이 구현에 위임한다. base 레이아웃은 styles/omForm.css 가 담당(antd CSS-in-JS 대체).
import {
  cloneElement,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  type CSSProperties,
  type ReactElement,
} from 'react'
import { useForm, Controller, type UseFormReturn } from 'react-hook-form'
import { z } from 'zod'
import { cx } from './cx'
import type {
  OmwColSize,
  OmwFormInstance,
  OmwFormItemProps,
  OmwFormLayout,
  OmwFormProps,
  OmwRule,
} from '../api/OmwForm'
import '../styles/omwForm.css'

export type { OmwColSize, OmwFormInstance, OmwFormLayout, OmwRule } from '../api/OmwForm'

type RhfValues = Record<string, unknown>

interface OmFormCtx {
  form: UseFormReturn<RhfValues>
  layout: OmwFormLayout
  labelCol?: OmwColSize
  wrapperCol?: OmwColSize
  requiredMark: boolean
}

const OmFormContext = createContext<OmFormCtx | null>(null)

const emailSchema = z.email()

/** antd rule.message 의 `${label}` 치환(login 계약). */
function resolveMessage(message: OmwRule['message'], label: unknown): string | undefined {
  if (message == null) return undefined
  if (typeof message !== 'string') return String(message)
  if (typeof label === 'string') return message.replace(/\$\{label\}/g, label)
  return message
}

function isEmpty(value: unknown): boolean {
  return value == null || value === '' || (Array.isArray(value) && value.length === 0)
}

/** antd rules 배열 → RHF validate 함수(zod 로 email 등 검사). */
function compileRules(
  rules: OmwRule[] | undefined,
  label: unknown,
): ((value: unknown) => true | string | Promise<true | string>) | undefined {
  if (rules == null || rules.length === 0) return undefined
  return async (value: unknown): Promise<true | string> => {
    for (const rule of rules) {
      const fallback = resolveMessage(rule.message, label) ?? ''
      if (rule.required === true && isEmpty(value)) return fallback
      if (rule.whitespace === true && typeof value === 'string' && value.trim() === '')
        return fallback
      if (rule.type === 'email' && typeof value === 'string' && value !== '') {
        if (!emailSchema.safeParse(value).success) return fallback
      }
      if (
        rule.pattern != null &&
        typeof value === 'string' &&
        value !== '' &&
        !rule.pattern.test(value)
      ) {
        return fallback
      }
      if (rule.len != null && typeof value === 'string' && value.length !== rule.len)
        return fallback
      if (rule.min != null && typeof value === 'string' && value.length < rule.min) return fallback
      if (rule.max != null && typeof value === 'string' && value.length > rule.max) return fallback
      if (typeof rule.validator === 'function') {
        try {
          await rule.validator(rule, value)
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err)
          return msg || fallback
        }
      }
    }
    return true
  }
}

/** 24 그리드 span → flex-basis 스타일(horizontal 라벨/컨트롤 폭). */
function colStyle(col: OmwColSize | undefined): CSSProperties | undefined {
  if (col == null) return undefined
  const span = typeof col === 'number' ? col : col.span
  if (span == null) return undefined
  const pct = `${(span / 24) * 100}%`
  return { flex: `0 0 ${pct}`, maxWidth: pct }
}

export const RhfForm = forwardRef<OmwFormInstance, OmwFormProps>(function RhfForm(props, ref) {
  const {
    layout = 'horizontal',
    labelCol,
    wrapperCol,
    requiredMark = true,
    hideRequiredMark,
    initialValues,
    onFinish,
    onFinishFailed,
    onValuesChange,
    className,
    style,
    children,
  } = props

  const form = useForm<RhfValues>({
    defaultValues: (initialValues ?? {}) as RhfValues,
    mode: 'onChange',
  })

  const handleFinish = useMemo(() => (values: RhfValues) => onFinish?.(values), [onFinish])
  const handleFail = useMemo(
    () => () => onFinishFailed?.({ values: form.getValues(), errorFields: [] }),
    [onFinishFailed, form],
  )

  // onValuesChange: (변경분, 전체값). antd 계약 재현.
  useEffect(() => {
    if (onValuesChange == null) return
    const sub = form.watch((all, { name }) => {
      const changed: RhfValues = name != null ? { [name]: form.getValues(name) } : {}
      onValuesChange(changed, all as RhfValues)
    })
    return () => sub.unsubscribe()
  }, [form, onValuesChange])

  useImperativeHandle(
    ref,
    (): OmwFormInstance => ({
      getFieldsValue: () => form.getValues(),
      getFieldValue: (name) => form.getValues(name),
      setFieldsValue: (values) => {
        Object.entries(values).forEach(([key, value]) =>
          form.setValue(key, value, { shouldDirty: true, shouldValidate: false }),
        )
      },
      setFieldValue: (name, value) =>
        form.setValue(name, value, { shouldDirty: true, shouldValidate: false }),
      getFieldError: (name) => {
        const error = form.getFieldState(name).error
        return error?.message != null ? [String(error.message)] : []
      },
      getFieldInstance: (name) => ({ focus: () => form.setFocus(name) }),
      resetFields: (names) => {
        if (names == null) {
          form.reset(initialValues ?? {})
        } else {
          names.forEach((name) => form.resetField(name))
        }
      },
      submit: () => void form.handleSubmit(handleFinish, handleFail)(),
      validateFields: async () => {
        const valid = await form.trigger()
        if (!valid) throw form.formState.errors
        return form.getValues()
      },
    }),
    [form, handleFinish, handleFail, initialValues],
  )

  const ctx = useMemo<OmFormCtx>(
    () => ({
      form,
      layout,
      labelCol,
      wrapperCol,
      requiredMark: requiredMark && hideRequiredMark !== true,
    }),
    [form, layout, labelCol, wrapperCol, requiredMark, hideRequiredMark],
  )

  return (
    <OmFormContext.Provider value={ctx}>
      <form
        className={cx('om-form', `om-form-${layout}`, className)}
        style={style}
        data-testid={props['data-testid']}
        onSubmit={form.handleSubmit(handleFinish, handleFail)}
      >
        {children}
      </form>
    </OmFormContext.Provider>
  )
})

/** 레이아웃 래퍼(라벨 그리드 + 컨트롤 + 에러). */
function FormItemLayout(props: {
  ctx: OmFormCtx | null
  label?: OmwFormItemProps['label']
  extra?: OmwFormItemProps['extra']
  className?: string
  style?: CSSProperties
  hidden?: boolean
  error?: string
  control: ReactElement
}): ReactElement {
  const { ctx, label, extra, className, style, hidden, error, control } = props
  const layout = ctx?.layout ?? 'horizontal'
  const labelStyle = layout === 'horizontal' ? colStyle(ctx?.labelCol) : undefined
  const wrapperStyle = layout === 'horizontal' ? colStyle(ctx?.wrapperCol) : undefined
  // antd Form.Item hidden: 값 등록은 유지하고 표시만 숨김.
  const rootStyle = hidden === true ? { ...style, display: 'none' } : style

  return (
    <div className={cx('om-form-item', className)} style={rootStyle}>
      <div className="om-form-item-row">
        {label != null && (
          <div className="om-form-item-label" style={labelStyle}>
            <label>{label}</label>
          </div>
        )}
        <div className="om-form-item-control" style={wrapperStyle}>
          <div className="om-form-item-control-input">
            <div className="om-form-item-control-input-content">{control}</div>
          </div>
          {error != null && error !== '' && (
            <div className="om-form-item-explain-error">{error}</div>
          )}
          {extra != null && <div className="om-form-item-extra">{extra}</div>}
        </div>
      </div>
    </div>
  )
}

export function RhfFormItem(props: OmwFormItemProps): ReactElement {
  const {
    name,
    label,
    rules,
    valuePropName = 'value',
    initialValue,
    extra,
    noStyle,
    hidden,
    className,
    style,
    children,
  } = props
  const ctx = useContext(OmFormContext)

  // 바인딩 없는 순수 레이아웃 항목(name 부재).
  if (name == null || ctx == null) {
    if (noStyle === true) return children
    return (
      <FormItemLayout
        ctx={ctx}
        label={label}
        extra={extra}
        className={className}
        style={style}
        hidden={hidden}
        control={children}
      />
    )
  }

  // antd 파리티: rule.message 의 `${label}` 치환값은 label(문자열) → 없으면 필드 name 으로 폴백.
  const labelForMessage =
    typeof label === 'string' ? label : typeof name === 'string' ? name : undefined
  const validate = compileRules(rules, labelForMessage)

  return (
    <Controller
      name={name}
      control={ctx.form.control}
      defaultValue={initialValue as never}
      rules={validate != null ? { validate } : undefined}
      render={({ field, fieldState }) => {
        const childProps = children.props as {
          onChange?: (arg: unknown) => void
          onBlur?: (arg: unknown) => void
        }
        const childOnChange = childProps.onChange
        const childOnBlur = childProps.onBlur
        const injected: Record<string, unknown> = {
          [valuePropName]: field.value ?? (valuePropName === 'checked' ? false : ''),
          onChange: (arg: unknown) => {
            field.onChange(arg)
            childOnChange?.(arg)
          },
          onBlur: (arg: unknown) => {
            field.onBlur()
            childOnBlur?.(arg)
          },
        }
        if (fieldState.error != null) injected.invalid = true
        const control = cloneElement(children, injected)
        const error =
          fieldState.error?.message != null ? String(fieldState.error.message) : undefined

        if (noStyle === true) return control
        return (
          <FormItemLayout
            ctx={ctx}
            label={label}
            extra={extra}
            className={className}
            style={style}
            hidden={hidden}
            error={error}
            control={control}
          />
        )
      }}
    />
  )
}
